import { Reveal } from "@/components/Reveal";

const Label = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
    {children}
  </span>
);

const STAGES = [
  {
    n: "01",
    t: "Deep Research",
    d: "We map your market, your buyers, and your competitors — a deep dive into the psychology behind why your customers actually buy.",
    m: "buyer psychology · competitor mapping · market analysis",
  },
  {
    n: "02",
    t: "Niche Content Analysis",
    d: "We analyze 1,000+ pieces already winning in your niche to find the formats proven to convert — so the plan is built on evidence, not hope.",
  },
  {
    n: "03",
    t: "Strategy & Proposal",
    d: "Buyer psychology and triggers are mapped onto concrete formats: a strategy built around your product, never a template.",
  },
];

const MINDS = [
  { name: "Seth Godin", principle: "Be remarkable, or be invisible." },
  { name: "Alex Hormozi", principle: "Make the offer so good that saying no feels irrational." },
  { name: "Russell Brunson", principle: "Turn one sale into a ladder of them." },
  { name: "Donald Miller", principle: "Make the message impossible to misread." },
  { name: "Gary Vaynerchuk", principle: "Win attention where it's still cheap." },
  { name: "Sabri Suby", principle: "Lead with value, not a pitch." },
  { name: "Eugene Schwartz", principle: "Meet the market at the awareness it already has." },
  { name: "Jay Abraham", principle: "Find the growth already hiding in the business." },
];

const DIMENSIONS = [
  { label: "Offer Quality", q: "Is the offer strong enough to make buying the obvious choice?" },
  { label: "Message Match", q: "Does the message land instantly with the right buyer?" },
  { label: "Funnel Depth", q: "Is there a path from first touch to repeat customer, not just a single ask?" },
  { label: "Channel Coverage", q: "Are you showing up where your customers actually pay attention?" },
  { label: "Velocity", q: "How fast can you get to market and improve once you're there?" },
  { label: "Automation", q: "What runs reliably every time, without a person dropping the ball?" },
];

export const MarketingDNAAudit = () => (
  <section
    id="marketing-dna-audit"
    className="scroll-mt-24 bg-[var(--bg-contrast)] py-28 md:py-36"
    data-testid="growth-marketing-dna-audit"
  >
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      {/* Intro */}
      <div className="max-w-3xl">
        <Reveal><Label>The Marketing DNA Audit</Label></Reveal>
        <Reveal delay={80}>
          <h2 className="mt-7 t-section">Your plan starts with an audit, not a guess.</h2>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
            Most agencies build a plan on instinct and hope. We start with a deep
            audit of your market, your buyers, and the content already winning in
            your niche — then validate every move against formulas that are
            already proven. By the time we make anything, the guesswork is gone.
          </p>
        </Reveal>
      </div>

      {/* Movement 1 — the audit */}
      <div className="mt-20">
        <Reveal>
          <h3 className="text-2xl font-semibold tracking-[-0.02em] text-[var(--ink)] sm:text-3xl">
            First, we run the audit.
          </h3>
        </Reveal>
        <ol className="mt-10 grid gap-6 md:grid-cols-3">
          {STAGES.map((s, i) => (
            <Reveal key={s.n} delay={i * 90}>
              <li className="flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-base)] p-7">
                <span className="text-3xl font-semibold tracking-[-0.03em] text-[var(--ember)]/40">{s.n}</span>
                <h4 className="mt-5 text-lg font-semibold tracking-[-0.02em] text-[var(--ink)]">{s.t}</h4>
                <p className="mt-3 flex-1 text-[15px] leading-relaxed text-[var(--muted)]">{s.d}</p>
                {s.m && (
                  <p className="mt-5 text-[13px] font-medium uppercase tracking-[0.08em] text-[var(--muted)]/70">
                    {s.m}
                  </p>
                )}
              </li>
            </Reveal>
          ))}
        </ol>
      </div>

      {/* Movement 2 — the validation */}
      <div className="mt-24">
        <Reveal>
          <h3 className="max-w-2xl text-2xl font-semibold tracking-[-0.02em] text-[var(--ink)] sm:text-3xl">
            Then we validate it against people who've already won.
          </h3>
        </Reveal>
        <Reveal delay={80}>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
            Applying the principles of —
          </p>
        </Reveal>
        <ul className="mt-10 grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
          {MINDS.map((m) => (
            <li key={m.name} className="border-t border-black/10 pt-4">
              <span className="block text-[16px] font-semibold tracking-[-0.01em] text-[var(--ink)]">
                {m.name}
              </span>
              <span className="mt-2 block text-[15px] leading-relaxed text-[var(--muted)]">
                {m.principle}
              </span>
            </li>
          ))}
        </ul>

        {/* Six dimensions — lighter supporting row */}
        <div className="mt-16 rounded-2xl border border-black/5 bg-[var(--bg-base)] p-8 sm:p-10">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--muted)]/70">
            Scored across six dimensions
          </p>
          <dl className="mt-7 grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
            {DIMENSIONS.map((d) => (
              <div key={d.label} className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--ember)]" />
                <div>
                  <dt className="text-[15px] font-semibold text-[var(--ink)]">{d.label}</dt>
                  <dd className="mt-1 text-[14px] leading-relaxed text-[var(--muted)]">{d.q}</dd>
                </div>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <Reveal delay={120}>
        <p className="mt-16 text-lg font-semibold tracking-tight text-[var(--ember)]">
          Nothing about the plan is a guess anymore. Now we build it.
        </p>
      </Reveal>
    </div>
  </section>
);
